/**
 * abilitySfxPlayer — Procedural Web Audio cast/hit tones (SPY-111).
 * Injectable playTone for tests; browser uses AudioContext oscillators.
 */
import { getAbilitySfxSpec } from './abilitySfxSpecs.js';
import { getAbilityVfxSpec } from '../vfx/abilityVfxSpecs.js';
import { isAudioMutedScale } from './isAudioMutedScale.js';

const IMPACT_KINDS = new Set(['burst', 'ground']);

function defaultPlayTone(tone, getContext) {
  if (!tone || typeof getContext !== 'function') return false;
  let ctx;
  try {
    ctx = getContext();
  } catch {
    return false;
  }
  if (!ctx || typeof ctx.createOscillator !== 'function') return false;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = tone.wave || 'sine';
  osc.frequency.value = tone.freqHz;
  const now = ctx.currentTime || 0;
  const dur = Math.max(0.02, (tone.durationMs || 80) / 1000);
  const vol = tone.gain ?? 0.1;
  gain.gain.setValueAtTime(vol, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + dur + 0.02);
  return true;
}

/**
 * @param {{
 *   playTone?: (tone: object) => boolean,
 *   getContext?: () => AudioContext | null,
 *   getVolumeScale?: () => number
 * }} [deps]
 */
export function createAbilitySfxPlayer(deps = {}) {
  const getContext = deps.getContext || (() => null);
  const getVolumeScale = deps.getVolumeScale || (() => 1);
  const playTone = deps.playTone || ((tone) => defaultPlayTone(tone, getContext));

  function scaledTone(tone) {
    const scale = Number(getVolumeScale()) || 0;
    const base = tone.gain ?? 0.1;
    return { ...tone, gain: base * Math.max(0, Math.min(1, scale)) };
  }

  function playRole(abilityId, role) {
    if (isAudioMutedScale(getVolumeScale())) return false;
    const spec = getAbilitySfxSpec(abilityId);
    if (!spec || !spec[role]) return false;
    return playTone(scaledTone({ ...spec[role], id: abilityId, role }));
  }

  function playCast(abilityId) {
    return playRole(abilityId, 'cast');
  }

  function playHit(abilityId) {
    return playRole(abilityId, 'hit');
  }

  /** Cast always; hit for burst/ground impact kinds (aura/projectile = cast only). */
  function playForCastResult(abilityId, vfxKindOrSpec = null) {
    const playedCast = playCast(abilityId);
    let kind = null;
    if (typeof vfxKindOrSpec === 'string') kind = vfxKindOrSpec;
    else if (vfxKindOrSpec && vfxKindOrSpec.kind) kind = vfxKindOrSpec.kind;
    else kind = getAbilityVfxSpec(abilityId)?.kind || null;
    if (IMPACT_KINDS.has(kind)) playHit(abilityId);
    return playedCast;
  }

  return { playCast, playHit, playForCastResult };
}
